import type { ReactNode } from 'react';
import LayersIcon from '@mui/icons-material/Layers';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FoundationIcon from '@mui/icons-material/Foundation';
import { mdiLandPlotsMarker, mdiFlask } from '@mdi/js';
import MdiIcon from '../../components/MdiIcon';
import type { AppAbility, Subject } from '@ast-data/shared';
import { scopedSubject } from '../auth/abilityContext';
import {
    WORKER_TIME_ENTRY_VARIANT,
    EQUIPMENT_TIME_ENTRY_VARIANT
} from '../timeTracking/timeEntryVariant';

/** The URL segment under /field/:projectId — also the category's identity. */
export type FieldCategoryKey =
    | 'findings'
    | 'overall-descriptions'
    | 'area-descriptions'
    | 'samples'
    | 'diaries'
    | 'time-entries'
    | 'equipment-time-entries';

/** The two Zeiterfassung categories, set apart from the recording ones. */
export const FIELD_TIME_ENTRY_KEYS: FieldCategoryKey[] = [
    'time-entries',
    'equipment-time-entries'
];

export interface FieldCategory {
    key: FieldCategoryKey;
    labelId: string;
    icon: ReactNode;
    /**
     * The entity this category records. A category is offered only where the
     * user may `create` it in that project, which keeps Zeiterfassung away from
     * Mitarbeiter without any special-casing here (unless `readableByAll`).
     */
    subject: Subject;
    /**
     * Shown to everyone who has the field app for the project at all, writable
     * or not. Such a category never makes a project show up on its own — see
     * hasAnyFieldCategory.
     */
    readableByAll?: boolean;
}

export const FIELD_CATEGORIES: FieldCategory[] = [
    {
        key: 'findings',
        labelId: 'nav.findings',
        icon: <LayersIcon />,
        subject: 'Finding'
    },
    {
        key: 'overall-descriptions',
        labelId: 'nav.overallDescriptions',
        icon: <FoundationIcon />,
        subject: 'OverallDescription'
    },
    {
        key: 'area-descriptions',
        labelId: 'nav.areaDescriptions',
        icon: <MdiIcon path={mdiLandPlotsMarker} />,
        subject: 'AreaDescription',
        // Written by the Teamleitung, read by the whole crew.
        readableByAll: true
    },
    {
        key: 'samples',
        labelId: 'nav.samples',
        icon: <MdiIcon path={mdiFlask} />,
        subject: 'Sample'
    },
    {
        key: 'diaries',
        labelId: 'nav.diaries',
        icon: <MenuBookIcon />,
        subject: 'Diary'
    },
    // Last, and set apart in the field menu (see useFieldNavGroups): the
    // workday, not the excavation. Both Zeiterfassungen record the same
    // TimeEntry subject, so they are offered (or withheld) together.
    {
        key: 'time-entries',
        labelId: WORKER_TIME_ENTRY_VARIANT.labelId,
        icon: <AccessTimeIcon />,
        subject: 'TimeEntry'
    },
    {
        key: 'equipment-time-entries',
        labelId: EQUIPMENT_TIME_ENTRY_VARIANT.labelId,
        icon: <PrecisionManufacturingIcon />,
        subject: 'TimeEntry'
    }
];

/** The category every project falls back to (and the one that always exists). */
export const DEFAULT_FIELD_CATEGORY: FieldCategoryKey = 'findings';

function canCreateInCategory(
    ability: AppAbility,
    category: FieldCategory,
    projectId: string
): boolean {
    return ability.can(
        'create',
        scopedSubject(category.subject, { projectId })
    );
}

/** Whether a project is worth offering in the field picker at all. */
export function hasAnyFieldCategory(
    ability: AppAbility,
    projectId: string
): boolean {
    return FIELD_CATEGORIES.some((category) =>
        canCreateInCategory(ability, category, projectId)
    );
}

export function canUseFieldCategory(
    ability: AppAbility,
    category: FieldCategory,
    projectId: string
): boolean {
    if (category.readableByAll) {
        return hasAnyFieldCategory(ability, projectId);
    }
    return canCreateInCategory(ability, category, projectId);
}

export function allowedFieldCategories(
    ability: AppAbility,
    projectId: string
): FieldCategory[] {
    return FIELD_CATEGORIES.filter((category) =>
        canUseFieldCategory(ability, category, projectId)
    );
}

/**
 * Keep the current category when switching project, falling back when the new
 * project doesn't permit it (e.g. a Teamleitung on one project, Mitarbeiter on
 * another, switching while in Zeiterfassung).
 */
export function fieldCategoryPath(
    ability: AppAbility,
    projectId: string,
    preferred: FieldCategoryKey | null
): string {
    const allowed = allowedFieldCategories(ability, projectId);
    const keep = allowed.some((category) => category.key === preferred);
    const key = keep && preferred ? preferred : DEFAULT_FIELD_CATEGORY;
    return `/field/${projectId}/${key}`;
}

/** A category key from untrusted text (a URL segment or query value). */
export function fieldCategoryFromKey(
    key: string | null
): FieldCategoryKey | null {
    return FIELD_CATEGORIES.some((category) => category.key === key)
        ? (key as FieldCategoryKey)
        : null;
}

/** The category segment of a field path, if it's on one. */
export function fieldCategoryFromPath(
    pathname: string
): FieldCategoryKey | null {
    const match = pathname.match(/^\/field\/[^/]+\/([^/]+)/);
    return fieldCategoryFromKey(match ? match[1] : null);
}
